import React, { useEffect, useState } from 'react';

/**
 * =========================================================================
 * VERCEL 402: PAYMENT_REQUIRED (PIXEL-PERFECT REPLICA)
 * =========================================================================
 * - Exact layout, colors (#ed367f, #59b89c, #333, #000) and weights
 * - ID stays on the exact same line right next to "ID: " (no line break)
 * - Zero animations / zero jumps on refresh: static, instant rendering
 * - Console logs persistent 402 status error
 */

export const IS_VERCEL_BLOCK_ACTIVE = false;

const generateVercelId = () => {
  if (typeof window !== 'undefined') {
    if (window.__vclCurrentId) return window.__vclCurrentId;
    if (window.__vclSyncId) return window.__vclSyncId;
  }
  const chars = '0123456789abcdefghijklmnopqrstuvwxyz';
  const rand = (n) => Array.from({ length: n }, () => chars[Math.floor(Math.random() * chars.length)]).join('');
  const id = `bom1::${rand(5)}-${Date.now()}-${rand(12)}`;
  if (typeof window !== 'undefined') window.__vclCurrentId = id;
  return id;
};
export default function DeploymentDisabled() {
  const [vercelId] = useState(() => generateVercelId());

  useEffect(() => {
    const prevTitle = document.title;
    document.title = '402: PAYMENT_REQUIRED';

    // Clear website favicon so browser tab displays the default internet/globe icon (like real Vercel)
    const links = document.querySelectorAll("link[rel*='icon']");
    links.forEach(l => l.remove());

    return () => {
      document.title = prevTitle || 'Lucy Garden';
      if (!document.querySelector("link[rel*='icon']")) {
        const link1 = document.createElement('link');
        link1.rel = 'icon';
        link1.href = '/favicon.ico';
        link1.sizes = '48x48';
        document.head.appendChild(link1);

        const link2 = document.createElement('link');
        link2.rel = 'icon';
        link2.type = 'image/png';
        link2.sizes = '192x192';
        link2.href = '/logo192.png';
        document.head.appendChild(link2);

        const link3 = document.createElement('link');
        link3.rel = 'apple-touch-icon';
        link3.sizes = '180x180';
        link3.href = '/apple-touch-icon.png';
        document.head.appendChild(link3);
      }
    };
  }, []);

  return (
    <div className="vcl-root">
      <style>{`
        ::selection {
          background: #79ffe1 !important;
        }
        ::-moz-selection {
          background: #79ffe1 !important;
        }
        .vcl-root *::selection {
          background: #79ffe1 !important;
        }
        .vcl-root *::-moz-selection {
          background: #79ffe1 !important;
        }
        .vcl-root {
          position: fixed;
          top: 0;
          left: 0;
          right: 0;
          bottom: 0;
          width: 100vw;
          height: 100vh;
          z-index: 2147483647;
          background: #ffffff !important;
          color: #000000 !important;
          margin: 0;
          padding: 0;
          display: flex;
          flex-direction: column;
          justify-content: center;
          overflow-y: auto;
          box-sizing: border-box;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
          font-size: 16px;
          line-height: 1.65;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }
        .vcl-root * {
          box-sizing: border-box;
        }
        .vcl-main {
          max-width: 800px;
          width: 100%;
          padding: 40px 60px;
          margin: auto;
        }
        .vcl-error-title {
          font-size: 20px;
          padding-left: 22px;
          line-height: 1.5;
          margin: 0 0 24px 0;
          color: #333333;
        }
        .vcl-error-title strong {
          font-weight: 700;
          color: #000000;
        }
        .vcl-error-title span {
          font-weight: 400;
          color: #333333;
        }
        .vcl-error-title-guilty {
          border-left: 2px solid #ed367f;
        }
        .vcl-error-title-innocent {
          border-left: 2px solid #59b89c;
        }
        .vcl-devinfo-container {
          border: 1px solid #dddddd;
          border-radius: 4px;
          padding: 20px;
          display: flex;
          flex-direction: column;
          margin: 0 0 32px 0;
          background: #ffffff;
        }
        .vcl-error-code {
          margin: 0 0 16px 0;
          font-size: 16px;
          line-height: 1.65;
          color: #000000;
          font-weight: 400;
        }
        .vcl-error-code strong {
          font-weight: 700;
          color: #000000;
        }
        .vcl-devinfo-line {
          color: #333333;
          font-size: 16px;
          line-height: 1.65;
          word-break: break-all;
        }
        .vcl-devinfo-line:not(:last-child) {
          margin-bottom: 8px;
        }
        .vcl-devinfo-line code {
          color: #000000;
          font-family: Menlo, Monaco, "Lucida Console", "Liberation Mono", "DejaVu Sans Mono", "Bitstream Vera Sans Mono", "Courier New", monospace, serif;
          font-size: 0.92em;
          font-weight: 400;
          display: inline;
          margin: 0;
          padding: 0;
          word-break: break-all;
        }
        .vcl-devinfo-line code::before,
        .vcl-devinfo-line code::after {
          content: '\`';
        }
        .vcl-list {
          padding: 0;
          margin: 0 0 32px 24px;
          list-style-type: none;
        }
        .vcl-list li {
          margin-bottom: 10px;
          color: #000000;
          font-size: 16px;
          line-height: 1.65;
          position: relative;
        }
        .vcl-list li::before {
          content: "–";
          display: inline-block;
          color: #cccccc;
          position: absolute;
          margin-left: -18px;
        }
        .vcl-link {
          cursor: pointer;
          color: #0070f3;
          text-decoration: none;
          border-bottom: 1px solid transparent;
        }
        .vcl-link:hover {
          border-bottom: 1px solid #0070f3;
        }

        /* Mobile layout (<= 500px) matching real Vercel */
        @media (max-width: 500px) {
          .vcl-main {
            padding: 10px 20px;
          }
          .vcl-error-title {
            font-size: 20px;
            padding-left: 22px;
            margin-bottom: 24px;
          }
          .vcl-devinfo-container {
            padding: 20px;
            margin-bottom: 0;
          }
          .vcl-devinfo-line {
            font-size: 14px;
            line-height: 1.55;
          }
          .vcl-devinfo-line:not(:last-child) {
            margin-bottom: 16px;
          }
          .vcl-devinfo-line code {
            font-size: 0.92em;
            display: inline;
            margin: 0;
          }
          .vcl-list {
            margin: 24px 0 0 24px;
          }
          .vcl-list li {
            font-size: 14px;
            line-height: 1.55;
          }
          .vcl-owner-error {
            display: none !important;
          }
        }
      `}</style>

      <main className="vcl-main">
        <div>
          <p className="vcl-error-title vcl-error-title-guilty">
            <strong>This Deployment</strong>
            <span> has been disabled.</span>
          </p>
          <p className="vcl-error-title vcl-error-title-innocent">
            <strong>Your connection</strong>
            <span> is working correctly.</span>
          </p>
          <p className="vcl-error-title vcl-error-title-innocent">
            <strong>Vercel</strong>
            <span> is working correctly.</span>
          </p>
        </div>

        <div className="vcl-devinfo-container">
          <span className="vcl-error-code">
            <strong>402</strong>: PAYMENT_REQUIRED
          </span>
          <span className="vcl-devinfo-line">
            Code: <code>DEPLOYMENT_DISABLED</code>
          </span>
          <span className="vcl-devinfo-line">
            ID: <code>{vercelId}</code>
          </span>
        </div>

        <ul className="vcl-list">
          <li>If you are a visitor, contact the website owner or try again later.</li>
          <li className="vcl-owner-error">
            If you are the owner, please{' '}
            <a
              className="vcl-link"
              href="https://vercel.com/docs/accounts/spend-management#pausing-projects"
              target="_blank"
              rel="noreferrer"
            >
              read this documentation section
            </a>.
          </li>
        </ul>
      </main>
    </div>
  );
}
